import { useRouterState } from "@tanstack/react-router";
import { useEffect, useSyncExternalStore } from "react";
import { type RecentVisit, recordVisit } from "./sidebar-model";

// What this browser remembers of the sidebar (ADR-114), stored locally like theme and density: the narrow column,
// the closed sections, the pinned places and queues, the recent visits. One small store over localStorage, so every
// component reading a key draws again when it changes, in this tab or in another.

const keys = {
  rail: "nexxt.sidebar",
  closed: "nexxt.sidebar.closed",
  pins: "nexxt.pins",
  recent: "nexxt.recent",
} as const;

type Key = (typeof keys)[keyof typeof keys];

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

function write(key: Key, value: string | null) {
  if (value === null) localStorage.removeItem(key);
  else localStorage.setItem(key, value);
  for (const listener of listeners) listener();
}

/** The raw text, a stable snapshot: parsing it on every read would hand React a new value each time. */
const useStored = (key: Key) => useSyncExternalStore(subscribe, () => localStorage.getItem(key));

function parseList<T>(raw: string | null): T[] {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) ? (value as T[]) : [];
  } catch {
    return [];
  }
}

const readList = <T>(key: Key) => parseList<T>(localStorage.getItem(key));

const toggleIn = (key: Key, id: string) => {
  const list = readList<string>(key);
  write(key, JSON.stringify(list.includes(id) ? list.filter((item) => item !== id) : [...list, id]));
};

export const useRail = () => useStored(keys.rail) === "rail";
export const toggleRail = () => write(keys.rail, localStorage.getItem(keys.rail) === "rail" ? null : "rail");

export const useClosedSections = () => parseList<string>(useStored(keys.closed));
export const toggleSection = (id: string) => toggleIn(keys.closed, id);
/** A section opens when its place comes on display, from ⌘K or a `g` shortcut: the place is always in view. */
export function openSection(id: string) {
  const closed = readList<string>(keys.closed);
  if (closed.includes(id)) write(keys.closed, JSON.stringify(closed.filter((item) => item !== id)));
}

/** Pins by stable id (`place:<id>`, `queue:<id>`), never by label: a renamed place stays pinned. */
export const usePins = () => parseList<string>(useStored(keys.pins));
export const togglePin = (pin: string) => toggleIn(keys.pins, pin);

export const useRecentVisits = () => parseList<RecentVisit>(useStored(keys.recent));
export function rememberVisit(visit: Omit<RecentVisit, "count" | "last">) {
  write(keys.recent, JSON.stringify(recordVisit(readList<RecentVisit>(keys.recent), visit, Date.now())));
}

/** The page of an entity counts as a visit, with its name, for ⌘K's recent pages; `place` gives icon and section. */
export function useRememberVisit(place: string, label: string) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    rememberVisit({ key: pathname, place, href: pathname, label });
  }, [pathname, place, label]);
}
